/* eslint-disable */
import { useState } from '@lib/systems/state'
import { fromIndex, distance } from '@lib/util/vector'
import { randomRange } from '@lib/util/random'
import { useCamera } from '@lib/systems/camera'

import { setContext, findPlayerPoms, findMoves, findPoms, movePomTo, clearSelected, nextTurn, skipTurn, endGame, countGains } from '@game/util/board'
import { useAnimations } from '@game/systems/animations'
import { pomScreenOffset, translatePomPos } from '@game/util/translatePomPos'
import { usePalette } from '@game/systems/palette'
import { useParticles } from '@game/systems/particles'

import {
  ROW_LENGTH,
  PLAYER_1,
  PLAYER_2,
  CPU_TURN_DELAY,
  CPU_MIN_THINK_DELAY,
  CPU_MAX_THINK_DELAY,
  JUMP,
  SPLIT
} from '@game/constants'

const {
  jiggle,
  clearSelection
} = useAnimations()

const {
  panCameraTo
} = useCamera()

const {
  currentPalette
} = usePalette()

const {
  emit
} = useParticles()

function distanceTo (a, b) {
  a = fromIndex(a, ROW_LENGTH)
  b = fromIndex(b, ROW_LENGTH)

  return distance(a, b)
}

function findClosestPom (pos, otherPlayer) {
  const state = useState()

  let closestDist = 999999999

  state.board.poms.forEach((pom, i) => {
    if (pom !== otherPlayer) return

    const dist = distanceTo(pos, i)

    if (dist < closestDist) {
      closestDist = dist
    }
  })

  return closestDist
}

// function getWeight (a, b, mod = 1, amt) {
//   return a > b
//     ? -((amt) || (a * mod))
//     : a < b
//       ? (amt || (b * mod))
//       : 0
// }

function wait (duration) {
  return new Promise((resolve) => {
    setTimeout(function () {
      resolve()
    }, duration)
  })
}

class ComputerPlayer {
  constructor () {
    this.resolving = false
  }

  async resolveTurn (lastSelected) {
    if (this.resolving) return []
    this.resolving = true

    const state = useState()
    setContext(state.board)

    const pal = currentPalette()

    await wait(CPU_TURN_DELAY)

    const myPoms = findPlayerPoms(state.board.whoseTurn)
    panCameraTo(pomScreenOffset(myPoms[Math.round(Math.random() * (myPoms.length - 1))]))

    await wait(randomRange(CPU_MIN_THINK_DELAY, CPU_MAX_THINK_DELAY))

    // Check if can move

    if (!myPoms.length) {
      state.board.numSkippedTurns++
      nextTurn()
      this.resolving = false
      return []
    }

    // Find all available moves

    let choices = findMoves(state.board.whoseTurn)
    if (!choices.length) {
      state.board.numSkippedTurns++
      nextTurn()
      this.resolving = false
      return []
    }

    const otherPlayer = state.board.whoseTurn === PLAYER_1
      ? PLAYER_2
      : PLAYER_1
    const otherPoms = findPlayerPoms(otherPlayer)

    // Calculate move statistics

    choices = choices.map((choice) => {
      choice.jumpTo = choice.jumpTo.map((to) => {
        const closest = findClosestPom(to, otherPlayer)
        const center = distanceTo(to, Math.round(ROW_LENGTH / 2))

        const threats = findPoms(to, true).canSplitTo.reduce((acc, pom) => {
          const enemyPoms = findPoms(pom, true, otherPlayer)
          return acc.concat(enemyPoms.canSplitTo, enemyPoms.canJumpTo)
        }, [])

        return {
          to,
          from: choice.pom,
          kind: JUMP,
          gain: countGains(to, otherPlayer),
          dist: Math.min(closest, center),
          closest,
          center,
          threats
        }
      })

      choice.splitTo = choice.splitTo.map((to) => {
        const closest = findClosestPom(to, otherPlayer)
        const center = distanceTo(to, Math.round(ROW_LENGTH / 2))

        const threats = findPoms(to, true).canSplitTo.reduce((acc, pom) => {
          const enemyPoms = findPoms(pom, true, otherPlayer)
          return acc.concat(enemyPoms.canSplitTo, enemyPoms.canJumpTo)
        }, [])

        return {
          to,
          from: choice.pom,
          kind: SPLIT,
          gain: countGains(to, otherPlayer) + 1,
          dist: Math.min(closest, center),
          closest,
          center,
          threats
        }
      })

      return choice
    })

    // Weight moves

    const difficulty = 0.875
    const aggressiveness = 0.225

    const moves = []
    choices.forEach(choice => {
      choice.jumpTo.forEach((m) => moves.push(m))
      choice.splitTo.forEach((m) => moves.push(m))
    })

    moves.sort((a, b) => {
      /*
        Difficulty/strategy metrics
        - Favor moves that net higher gains
        - Favor moving towards the opponent and center
        - Favor splitting over jumping
        - Random chance defined by difficulty settings
        Aggressive Strategies
        - More likely to move towards player
        - More likely to use jump moves
        Defensive Strategies
        - More likely to move towards center
        - More likely to use split moves
      */

      let aw = 0
      let bw = 0
      const aggroMod = ((Math.random() * (1 - difficulty)) * (Math.random() > 0.5 ? 0.5 : -0.5)) + aggressiveness

      // Try to avoid making moves that will kill you
      if (myPoms.length < otherPoms.length * (2 - aggressiveness)) {
        if (a.threats.length || b.threats.length) console.log('danger')
        if (a.kind === JUMP) aw -= a.threats.length / 2
        if (b.kind === JUMP) bw -= b.threats.length / 2
        if (a.kind === SPLIT) aw -= a.threats.length / 4
        if (b.kind === SPLIT) bw -= b.threats.length / 4
      }

      // Try to make the most gains every move
      aw += a.gain * (a.gain >= otherPoms.length ? 10 : 1)
      bw += b.gain * (b.gain >= otherPoms.length ? 10 : 1)

      // Favor splitting when defensive
      if (aggroMod < 0) {
        if (a.kind === SPLIT) aw += 1
        if (b.kind === SPLIT) bw += 1
      }

      // Favor splitting when less than 3 poms
      if (Math.random() > (1 - difficulty) && myPoms.length < 3) {
        if (a.kind === SPLIT) aw += 1
        if (b.kind === SPLIT) bw += 1
      }

      // Favor the center/opponent
      if (aggroMod >= 0.33) {
        a.closest < b.closest
          ? aw += 1
          : bw += 1
      } else if (aggroMod >= -0.66) {
        a.center < b.center
          ? aw += 1
          : bw += 1
      }

      return bw - aw
    })

    // Pick final move
    moves.length = 3 + Math.round((1 - difficulty) * 3) // best moves
    for (let i = 0; i < Math.round(difficulty * 6); i++) {
      moves.unshift(moves[0])
    }
    const moveIndex = Math.round( // pick randomly, with more chance for variance at lower difficulties
      Math.random() *
      (
        (1 - difficulty) *
        (moves.length - 1)
      )
    )
    const move = moves[moveIndex]

    // Do move

    state.board.selected = move.from
    findPoms(move.from)
    jiggle(move.from, 3)
    panCameraTo(pomScreenOffset(move.from))
    const pos = translatePomPos(move.from)
    emit(
      [3, 5],
      pos.x, pos.y,
      [pal.accent, state.board.poms[move.from] === PLAYER_1 ? pal.player1 : pal.player2],
      {
        velx: (Math.random() * 2) + 1,
        vely: (Math.random() * 2) + 1
      }
    )

    await wait(randomRange(CPU_MIN_THINK_DELAY, CPU_MAX_THINK_DELAY))
    panCameraTo(pomScreenOffset(move.to))

    const res = [move.to, ...movePomTo(move.to, true, move.kind, move.from)]
    clearSelection()
    clearSelected()

    // Check win conditions
    const canMove = !!findMoves(state.board.whoseTurn, true)
      .reduce((acc, item) => acc + item.jumpTo.length + item.splitTo.length, 0)
    const otherCanMove = !!findMoves(otherPlayer, true)
      .reduce((acc, item) => acc + item.jumpTo.length + item.splitTo.length, 0)

    wait(CPU_TURN_DELAY).then(() => {
      if (!canMove || !otherCanMove) {
        endGame()
      } else {
        panCameraTo(pomScreenOffset(lastSelected))
        nextTurn()
      }
      this.resolving = false
    })

    return res
  }
}

export const computer = new ComputerPlayer()
export const resolveTurn = computer.resolveTurn.bind(computer)
